// UI strings for the rising word pace (Axel, 2026-10-14; docs/COURSE.md "Pacing", engine/src/course.ts
// paceSteps / nextRateRise): the frontier line ("6 uutta sanaa päivässä · seuraava nousu 25.12."), the
// step table and the staircase fields in Asetukset → the pace and the admin's pace form.
// Finnish (default) and English side by side; en has the same shape.
// Style: docs/FINNISH-STYLE.md (sinä/te, no internal jargon, only actionable text, dates 25.12.).
import { defineArea } from '../area';

const fiWords = (n: number) => `${n} ${n === 1 ? 'uusi sana' : 'uutta sanaa'}`;
/** "yhdellä", "kahdella" … for the step size (a number past ten). Not "yhdellä sanalla": that reads as the idiom "in a word". */
const FI_ADESSIVE = [
  'nollalla',
  'yhdellä',
  'kahdella',
  'kolmella',
  'neljällä',
  'viidellä',
  'kuudella',
  'seitsemällä',
  'kahdeksalla',
  'yhdeksällä',
  'kymmenellä',
];
const fiBy = (n: number) => FI_ADESSIVE[n] ?? String(n);

export const wordPaceFi = {
  /** The frontier line's rate: "6 uutta sanaa päivässä". */
  rate: (n: number) => `${fiWords(n)} päivässä`,
  /** … and when it next goes up (date as 25.12.). */
  nextRise: (date: string) => `seuraava nousu ${date}`,
  /** A due rise that waits (engine riseGate): why, in a few words. */
  held: {
    overdue: 'nousu odottaa, kunnes rästit on kerrattu',
    recall: 'nousu odottaa, kunnes kortit jäävät taas paremmin mieleen',
    unknown: 'nousu odottaa lisää kertauksia',
  },
  /** Asetukset → the pace: the staircase in one sentence (replaces the flat "sen jälkeen 9 päivässä"). */
  stairs: (first: number, days: number, start: number, step: number, everyDays: number, top: number, until: string) =>
    `Ensimmäiset ${days} päivää: ${fiWords(first)} päivässä, sen jälkeen ${start}. Noin ${everyDays} päivän välein tahti nousee ${fiBy(step)}, kunnes päivässä on ${fiWords(top)} (viimeiset uudet sanat ${until}).`,
  /** The gate, in one sentence. */
  gate: (percent: number, overdue: number) =>
    `Tahti nousee vain, jos olette kumpikin muistaneet vähintään ${percent} % kahden viimeisen viikon kertauksista eikä kummallakaan ole yli ${overdue} rästikorttia. Muuten tahti pysyy ennallaan.`,
  /** The step table. */
  table: 'Uusia sanoja päivässä',
  from: 'Alkaen',
  to: 'Asti',
  perDay: 'Päivässä',
  now: 'nyt',
  waits: 'odottaa',
  /** The admin's fields. */
  fieldStart: 'Uusia sanoja päivässä alkujakson jälkeen',
  fieldStep: 'Nousu kerrallaan (sanaa päivässä)',
  fieldSteps: 'Portaita alkujakson jälkeen',
  fieldRecall: 'Nousuun vaadittava muistamisprosentti',
};

const enWords = (n: number) => `${n} new ${n === 1 ? 'word' : 'words'}`;

const wordPaceEn: typeof wordPaceFi = {
  rate: (n) => `${enWords(n)} a day`,
  nextRise: (date) => `next rise ${date}`,
  held: {
    overdue: 'the rise waits until the overdue cards are done',
    recall: 'the rise waits until the cards stick better again',
    unknown: 'the rise waits for more reviews',
  },
  stairs: (first, days, start, step, everyDays, top, until) =>
    `The first ${days} days: ${enWords(first)} a day, then ${start}. About every ${everyDays} days the pace rises by ${step}, until it is ${enWords(top)} a day (last new words ${until}).`,
  gate: (percent, overdue) =>
    `The pace rises only if you have both recalled at least ${percent} % of your reviews over the last two weeks and neither has more than ${overdue} overdue cards. Otherwise the pace stays as it is.`,
  table: 'New words a day',
  from: 'From',
  to: 'To',
  perDay: 'A day',
  now: 'now',
  waits: 'waits',
  fieldStart: 'New words a day after the first period',
  fieldStep: 'Rise each time (words a day)',
  fieldSteps: 'Stairs after the first period',
  fieldRecall: 'Recall needed for a rise (%)',
};

// Messages.wordPace (i18n/area.ts: collected by area-registry.ts)
export const area = defineArea('wordPace', wordPaceFi, wordPaceEn);
declare module '../area' {
  interface AreaMessages extends Record<typeof area.key, typeof wordPaceFi> {}
}
